import { inject, Injectable } from "@angular/core";
import { SessionServiceProxy, TenantServiceProxy } from "@api/core";
import {
    UserLoginInfoDto,
    TenantLoginInfoDto,
    ApplicationInfoDto,
    GetCurrentLoginInformationsOutput,
    SessionTenantInfo,
} from "@api/core.models";
import { AuthenticateResultModel } from "@api/shared-models";
import { BehaviorSubject, Subscription } from "rxjs";
import { AbpMultiTenancyService } from "abp-ng2-module";
import { Router } from "@angular/router";
import { AppAuthService } from "./app-auth.service";
import { AppConsts } from "../helpers/AppConsts";
import { TenantDebtLock } from "./tenant-debt-lock";

@Injectable({
    providedIn: "root",
})
export class AppSessionService {
    private _tenantDebtLock = inject(TenantDebtLock);

    private _user: UserLoginInfoDto;

    private _tenant: TenantLoginInfoDto;

    private _application: ApplicationInfoDto;

    onSessionChanged: BehaviorSubject<any>;

    onTenantInfoChanged: BehaviorSubject<any>;

    tenantInfo: SessionTenantInfo;

    onTrialPayed: BehaviorSubject<SessionTenantInfo>;

    checkTrialSubscription: Subscription;

    welcomeMessageDisplayed: boolean;

    constructor(
        private _sessionService: SessionServiceProxy,
        private _authService: AppAuthService,
        private _tenantService: TenantServiceProxy,
        private _abpMultiTenancyService: AbpMultiTenancyService,
        private _router: Router
    ) {
        this.onSessionChanged = new BehaviorSubject(null);
        this.onTenantInfoChanged = new BehaviorSubject(null);
        this.onTrialPayed = new BehaviorSubject(null);

        this._authService.onLogin.subscribe((value) => {
            if (!!value && value) {
                this.welcomeMessageDisplayed = abp.setting.getBoolean("WelcomeMessageDisplayed");
                this.checkTrial();
            }
        });

        this._authService.onLogout.subscribe((value) => {
            this.onSessionChanged.next(null);
            this._user = null;
            if (!!value && value && !!this.checkTrialSubscription) {
                this.checkTrialSubscription.unsubscribe();
            }
        });
    }

    checkTrial() {
        this.checkTrialSubscription = this._tenantService.getSessionTenantInfo().subscribe((info: SessionTenantInfo) => {
            this.tenantInfo = info;
            this.onTenantInfoChanged.next(info);
            this.onTrialPayed.next(this.tenantInfo);
            if (abp.setting.get(AppConsts.Settings.ApplicationVersion) !== "1.0") {
                if (abp.setting.get(AppConsts.Settings.ApplicationVersion) === "2.0" && this.isLockedV2()) {
                    this._router.navigate(["/settings/subscription-details"]);
                }
                return;
            }
            this._tenantDebtLock.enforce(info);
        });
    }

    clearTrial() {
        this.tenantInfo.subscriptionInfo.status.code = 4;
        this.onTenantInfoChanged.next(this.tenantInfo);
        this.onTrialPayed.next(this.tenantInfo);
    }

    get application(): ApplicationInfoDto {
        return this._application;
    }

    get user(): UserLoginInfoDto {
        return this._user;
    }

    get userId(): number {
        return this.user ? this.user.id : null;
    }

    get tenant(): TenantLoginInfoDto {
        return this._tenant;
    }

    get tenantId(): number {
        return this.tenant ? this.tenant.id : null;
    }

    getShownLoginName(): string {
        let userName = this._user.userName;
        if (!this._abpMultiTenancyService.isEnabled) {
            return userName;
        }

        return (this._tenant ? this._tenant.tenancyName : ".") + "\\" + userName;
    }

    init(): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            this._sessionService
                .getCurrentLoginInformations()
                .toPromise()
                .then(
                    (result: GetCurrentLoginInformationsOutput) => {
                        this._application = result.application;
                        this._user = result.user;
                        localStorage.setItem(AppConsts.authorization.userIdStorageKey,this._user?.id.toString());
                        if (!!result.user) {
                            if (!!result.user.avatar) {
                                this._user.avatar = result.user.avatar + "?d=" + new Date().getTime();
                            }
                        }
                        this._tenant = result.tenant;
                        this.onSessionChanged.next(this._user);
                        resolve(true);
                    },
                    (err) => {
                        reject(err);
                    }
                );
        });
    }

    reloadTenantInfo(proposedName: string) {
        this.tenant.name = proposedName;
        this.onTenantInfoChanged.next(this.tenant);
        this._sessionService.getCurrentLoginInformations().subscribe((result: GetCurrentLoginInformationsOutput) => {
            this._tenant = result.tenant;
            this.onTenantInfoChanged.next(result.tenant);
        });
    }

    trialPayed(tenantInfo: SessionTenantInfo) {
        this.tenantInfo = tenantInfo;
        this.onTrialPayed.next(tenantInfo);
    }

    changeTenantIfNeeded(tenantId?: number): boolean {
        if (this.isCurrentTenant(tenantId)) {
            return false;
        }

        abp.multiTenancy.setTenantIdCookie(tenantId);
        location.reload();
        return true;
    }

    private isCurrentTenant(tenantId?: number) {
        if (!tenantId && this.tenant) {
            return false;
        } else if (tenantId && (!this.tenant || this.tenant.id !== tenantId)) {
            return false;
        }

        return true;
    }

    isUserInRoleTenant(role: string): boolean {
        return this.user && this.user.roleNames && this.user.roleNames.indexOf(role) >= 0;
    }

    isDoctor(): boolean {
        return this.isUserInRoleTenant(AppConsts.RolesNames.Doctor);
    }

    isAdmin(): boolean {
        return this.isUserInRoleTenant(AppConsts.RolesNames.Admin);
    }

    isEmployee(): boolean {
        return this.isUserInRoleTenant(AppConsts.RolesNames.Employee);
    }

    tenantHasAtLeastOneMobileApp(): boolean {
        return this.tenant.hasMobileApplication;
    }

    isImpersonating(): boolean {
        let impersonatorUserId = this._authService.getClaim(
            "http://www.aspnetboilerplate.com/identity/claims/impersonatorUserId"
        );
        return impersonatorUserId && Number.parseInt(impersonatorUserId) > 0;
    }

    isHostUser(): boolean {
        return !this.tenantId || this.isImpersonating();
    }

    checkSession(): Promise<boolean> {
        return this.init();
    }

    changeTenant(info: TenantLoginInfoDto): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            this._sessionService
                .changeTenant(info)
                .toPromise()
                .then(
                    (result: AuthenticateResultModel) => {
                        this._authService.processAuthenticateResult(result, true, AppConsts.appBaseUrl);
                        resolve(true);
                    },
                    (err) => {
                        reject(err);
                    }
                );
        });
    }

    isLocked() {
        return this.tenantInfo !== undefined && this.tenantInfo.subscriptionInfo?.status.code === 1;
    }

    isLockedV2() {
        return this.tenantInfo.subscriptionInfo?.status.code === 7;
    }

    clear() {
        this._user = null;
    }
}
